/**
 * Anomaly Detail Page
 *
 * Route: /anomalies/:id
 *
 * The counterpart to RecommendationDetailPage, and built the same way for the
 * same reasons: fetches its own anomaly by id rather than receiving it via
 * router state, so a direct link, a refresh, or a bookmark all work. A page
 * that only renders when you arrive from one particular list isn't a page,
 * it's a modal wearing a URL.
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertTriangle, Link2 } from 'lucide-react';
import { Anomaly, getAnomalyById } from '../services/anomalyService';
import { AnomalyDetailBody } from '../components/anomalies/AnomalyDetailBody';
import { CloudProviderIcon } from '../components/common/CloudProviderIcon';
import { Badge, BadgeVariant } from '../components/common/Badge';
import {
  confidenceLabel,
  formatRelativeDate,
  formatType,
  getSeverityVariant,
  getStatusVariant,
  getTypeVariant,
} from '../utils/anomalyDisplay';

const AnomalyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [anomaly, setAnomaly] = useState<Anomaly | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = React.useCallback(() => {
    if (!id) return;
    setLoading(true);
    setNotFound(false);
    setError(null);
    getAnomalyById(id)
      .then((data) => {
        if (data) setAnomaly(data);
        else setNotFound(true);
      })
      .catch((err: any) => {
        if (err?.response?.status === 404) setNotFound(true);
        else setError(err?.response?.data?.message || err?.message || 'Failed to load anomaly');
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const conf = anomaly ? confidenceLabel(anomaly) : null;

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      {/* Back affordance is fused into the title rather than floating above it
          as a separate link - one target, and the heading still reads as the
          heading. */}
      <div>
        <button
          onClick={() => navigate('/anomalies')}
          aria-label="Back to Anomalies"
          className="inline-flex items-center gap-2 text-lg font-semibold tracking-tight text-foreground transition-colors hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          Anomaly Details
        </button>
        {anomaly && (
          <p className="ml-6 truncate font-mono text-xs text-muted-foreground">{anomaly.id}</p>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading anomaly…
        </div>
      )}

      {!loading && notFound && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-sm text-amber-600 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          This anomaly no longer exists — it may have been resolved and archived.
        </div>
      )}

      {!loading && error && (
        <div className="flex items-center gap-3 rounded-lg border border-rose-500/20 bg-rose-500/5 px-4 py-3 text-sm text-rose-600 dark:text-rose-400">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {!loading && anomaly && (
        <>
          <div className="space-y-2">
            <p className="text-sm font-medium leading-relaxed text-foreground/90">{anomaly.message}</p>
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant={getSeverityVariant(anomaly.severity)}>{anomaly.severity.toUpperCase()}</Badge>
              <Badge variant={getTypeVariant(anomaly.type)}>{formatType(anomaly.type)}</Badge>
              <Badge variant={anomaly.provider as BadgeVariant} aria-label={`Provider: ${anomaly.provider.toUpperCase()}`}>
                <CloudProviderIcon provider={anomaly.provider} size={12} className="mr-1" />
                {anomaly.provider.toUpperCase()}
              </Badge>
              {anomaly.cluster_id && (
                <Badge variant="warning" title="Detected together with other anomalies in the same short window">
                  <Link2 className="mr-1 h-3 w-3" />
                  Clustered
                </Badge>
              )}
              {conf && (
                <Badge variant="muted" title={conf.title}>
                  {conf.text}
                </Badge>
              )}
              {anomaly.status !== 'open' && (
                <Badge variant={getStatusVariant(anomaly.status)}>
                  {anomaly.status.replace('_', ' ').toUpperCase()}
                </Badge>
              )}
              <span className="text-xs text-muted-foreground">{formatRelativeDate(anomaly.detected_at)}</span>
            </div>
          </div>

          <AnomalyDetailBody
            anomaly={anomaly}
            onRefresh={load}
            onActionComplete={() => navigate('/anomalies')}
          />
        </>
      )}
    </div>
  );
};

export default AnomalyDetailPage;
